import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── POS Sales History ───────────────────────────────────────
 * View all sales transactions with filters, daily summary,
 * and receipt reprint capability.
 */

import { useEffect, useState, useCallback } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardHeader, Badge, Button, Input } from '@repo/ui-core'
import { posSaleRepo } from '../../../lib/db'
import type { POSSale } from '@repo/entity-pos'
import {
  PAYMENT_METHOD_LABELS, PAYMENT_METHOD_COLORS,
  SALE_STATUS_LABELS, SALE_STATUS_COLORS,
} from '@repo/entity-pos'
import { POSSaleService } from '@repo/entity-pos'
import {
  Search, Receipt, TrendingUp, DollarSign,
  RefreshCw, ShoppingBag, Ban, ArrowLeftRight,
} from 'lucide-react'

export function POSSalesPage() {
  const [sales, setSales] = useState<POSSale[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [selectedSale, setSelectedSale] = useState<POSSale | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await posSaleRepo.findMany({
        page: 1,
        pageSize: 200,
        sort: [{ field: 'createdAt', direction: 'desc' }],
      })
      if ('items' in result) setSales(result.items as POSSale[])
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = sales.filter((s) => {
    if (search) {
      const q = search.toLowerCase()
      if (
        !s.invoiceNumber.toLowerCase().includes(q) &&
        !(s.customerName && s.customerName.toLowerCase().includes(q))
      ) return false
    }
    if (statusFilter && s.status !== statusFilter) return false
    if (dateFilter && s.saleDate !== dateFilter) return false
    return true
  })

  const summary = POSSaleService.computeDailySummary(filtered)

  const today = new Date().toISOString().slice(0, 10)
  const todaySales = filtered.filter((s) => s.saleDate === today && s.status === 'completed')
  const todaySummary = POSSaleService.computeDailySummary(todaySales)

  const handleVoid = async (sale: POSSale) => {
    if (!confirm(`Void sale ${sale.invoiceNumber}? This cannot be undone.`)) return
    try {
      await posSaleRepo.update(sale.id, {
        status: 'voided',
        voidReason: 'Voided from sales history',
        voidedAt: Date.now(),
        voidedBy: 'admin',
        version: sale.version,
      } as any)
      load()
    } catch (err) { console.error(err); alert(mapErrorToUserMessage(err).userMessage) }
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales History</h1>
          <p className="text-sm text-gray-500">POS transaction records</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} icon={<RefreshCw className="h-4 w-4" />}>Refresh</Button>
          <Link to="/pos/register">
            <Button icon={<ShoppingBag className="h-4 w-4" />}>POS Terminal</Button>
          </Link>
        </div>
      </div>

      {/* Today's Summary */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KpiCard icon={<ShoppingBag className="h-5 w-5 text-blue-600" />} label="Today's Sales" value={todaySummary.transactionCount} color="blue" />
        <KpiCard icon={<DollarSign className="h-5 w-5 text-green-600" />} label="Today's Revenue" value={`₱${todaySummary.totalSales.toLocaleString()}`} color="green" />
        <KpiCard icon={<Ban className="h-5 w-5 text-red-600" />} label="Today Voided" value={`₱${todaySummary.totalVoided.toLocaleString()}`} color="red" />
        <KpiCard icon={<ArrowLeftRight className="h-5 w-5 text-yellow-600" />} label="Today Refunded" value={`₱${todaySummary.totalRefunded.toLocaleString()}`} color="yellow" />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader title="Transactions" description={`${filtered.length} sales`} />
        <div className="mb-4 flex gap-3 px-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search invoice or customer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">All Statuses</option>
            {Object.entries(SALE_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-44"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-4 py-3 text-left font-medium text-gray-600">Invoice</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Date/Time</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Customer</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Items</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Total</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Payment</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No sales found</td></tr>
              ) : (
                filtered.map((sale) => (
                  <tr key={sale.id} className={`border-b hover:bg-gray-50 ${sale.status === 'voided' ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedSale(sale)}
                        className="font-mono text-xs font-medium text-blue-600 hover:underline"
                      >
                        {sale.invoiceNumber}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <p>{sale.saleDate}</p>
                      <p className="text-xs">{sale.saleTime}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-900">{sale.customerName || 'Walk-in'}</td>
                    <td className="px-4 py-3 text-right">{sale.itemCount}</td>
                    <td className="px-4 py-3 text-right font-bold">₱{sale.grandTotal.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge color={PAYMENT_METHOD_COLORS[sale.paymentMethod] as any || 'gray'} size="sm">
                        {PAYMENT_METHOD_LABELS[sale.paymentMethod]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge color={SALE_STATUS_COLORS[sale.status]} size="sm">
                        {SALE_STATUS_LABELS[sale.status]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {sale.status === 'completed' && (
                        <button
                          onClick={() => handleVoid(sale)}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Payment Method Breakdown */}
      <Card>
        <CardHeader title="Payment Method Breakdown" />
        <div className="grid grid-cols-4 gap-4 p-4">
          {(Object.keys(PAYMENT_METHOD_LABELS) as Array<keyof typeof PAYMENT_METHOD_LABELS>).map((method) => {
            const amount = summary.byPaymentMethod[method] || 0
            if (amount === 0) return null
            return (
              <div key={method} className="rounded-lg border p-3 text-center">
                <p className="text-xs text-gray-500">{PAYMENT_METHOD_LABELS[method]}</p>
                <p className="text-lg font-bold text-gray-900">₱{amount.toLocaleString()}</p>
              </div>
            )
          })}
        </div>
      </Card>

      {/* Sale Detail Modal */}
      {selectedSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedSale(null)}>
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b bg-white px-6 py-4">
              <div>
                <h3 className="text-lg font-bold">{selectedSale.invoiceNumber}</h3>
                <p className="text-sm text-gray-500">{selectedSale.saleDate} at {selectedSale.saleTime}</p>
              </div>
              <button onClick={() => setSelectedSale(null)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Cashier</span>
                <span>{selectedSale.cashierName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Customer</span>
                <span>{selectedSale.customerName || 'Walk-in'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Payment</span>
                <Badge color={PAYMENT_METHOD_COLORS[selectedSale.paymentMethod] as any || 'gray'} size="sm">
                  {PAYMENT_METHOD_LABELS[selectedSale.paymentMethod]}
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Status</span>
                <Badge color={SALE_STATUS_COLORS[selectedSale.status]} size="sm">
                  {SALE_STATUS_LABELS[selectedSale.status]}
                </Badge>
              </div>

              {selectedSale.discountLabel && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>Discount ({selectedSale.discountLabel})</span>
                  <span>-₱{selectedSale.discountTotal.toFixed(2)}</span>
                </div>
              )}

              <hr />

              {/* Line Items */}
              <div>
                <p className="mb-2 text-xs font-medium text-gray-500 uppercase">Items ({selectedSale.items?.length || 0})</p>
                <div className="space-y-2">
                  {(selectedSale.items || []).map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {item.quantity} × ₱{item.unitPrice.toFixed(2)}
                          {item.discountPercent > 0 && ` (${item.discountPercent}% off)`}
                        </p>
                      </div>
                      <p className="font-medium">₱{item.lineTotal.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <hr />

              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Subtotal</span>
                  <span>₱{selectedSale.subtotal.toFixed(2)}</span>
                </div>
                {selectedSale.discountTotal > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount</span>
                    <span>-₱{selectedSale.discountTotal.toFixed(2)}</span>
                  </div>
                )}
                {selectedSale.vatTotal > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>VAT</span>
                    <span>₱{selectedSale.vatTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 text-lg font-bold">
                  <span>Total</span>
                  <span>₱{selectedSale.grandTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tendered</span>
                  <span>₱{selectedSale.amountTendered.toFixed(2)}</span>
                </div>
                {selectedSale.changeDue > 0 && (
                  <div className="flex justify-between text-green-600 font-medium">
                    <span>Change</span>
                    <span>₱{selectedSale.changeDue.toFixed(2)}</span>
                  </div>
                )}
              </div>

              {selectedSale.status === 'voided' && selectedSale.voidReason && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  <strong>Voided:</strong> {selectedSale.voidReason}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function KpiCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string | number; color: string }) {
  const bgMap: Record<string, string> = { blue: 'bg-blue-50 border-blue-200', green: 'bg-green-50 border-green-200', red: 'bg-red-50 border-red-200', yellow: 'bg-yellow-50 border-yellow-200' }
  return (
    <div className={`rounded-xl border p-4 ${bgMap[color] || 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-1">{icon}<p className="text-xs text-gray-600">{label}</p></div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  )
}
